/**
 * Industry-specific default values for the TAS ROI Calculator
 * Based on global 3-year averages from industry benchmarks
 * Source: TAS Industry Defaults specification
 */

export interface IndustryDefault {
  // TAS Score metrics
  tasScore: number;
  topPerformerRange: [number, number];
  
  // Hiring metrics
  timeToFill: number; // days
  costPerHire: number; // dollars
  dailyVacancyCost: number; // dollars per day
  avgSalary: number; // dollars per year
  
  // Scheduling process metrics
  avgConfirmationTime: number; // hours
  adminTimePerHire: number; // minutes
  reschedulingRate: number; // percentage
  interviewNoShowRate: number; // percentage
  
  // Satisfaction metrics (1-10 scale)
  candidateSatisfaction: number;
  interviewerSatisfaction: number;
  interviewerAvailability: number;
  
  // Turnover rate for FTE/Annual Hires conversion
  turnoverRate: number; // Percentage as decimal (e.g., 0.60 for 60%)
  
  // Time-to-Value metrics
  implementationComplexity: 'simple' | 'standard' | 'complex';
  defaultImplementationWeeks: number;
  defaultAdoptionMonths: number;
  typicalRealizationFactor: number;
  ttfAttributionFactor: number;
  roleVariabilityDiscount: number;
}

export type IndustryName = 
  | "Technology"
  | "Financial Services"
  | "Healthcare Providers & Services"
  | "Pharmaceuticals & Life Sciences"
  | "Manufacturing"
  | "Retail & Consumer Goods"
  | "Professional & Business Services"
  | "Government & Public Sector"
  | "Education"
  | "Energy & Utilities"
  | "Transportation & Logistics"
  | "Telecommunications"
  | "Hospitality & Travel"
  | "Media & Entertainment"
  | "Construction & Engineering"
  | "Non-Profit & NGOs";

export interface IndustryInfo {
  name: IndustryName;
  description: string;
}

export const INDUSTRY_DESCRIPTIONS: IndustryInfo[] = [
  { 
    name: "Technology", 
    description: "Software, hardware, IT services, cloud, AI, cybersecurity" 
  },
  { 
    name: "Financial Services", 
    description: "Banking, insurance, investment, asset management, fintech" 
  },
  { 
    name: "Healthcare Providers & Services", 
    description: "Hospitals, clinics, health systems, telehealth, medical services" 
  },
  { 
    name: "Pharmaceuticals & Life Sciences", 
    description: "Biotech, pharmaceuticals, medical devices, diagnostics" 
  },
  { 
    name: "Manufacturing", 
    description: "Industrial, electronics, automotive, aerospace manufacturing" 
  },
  { 
    name: "Retail & Consumer Goods", 
    description: "Physical retail, e-commerce, consumer products, fashion, CPG" 
  },
  { 
    name: "Professional & Business Services", 
    description: "Consulting, legal, accounting, staffing, outsourcing, marketing agencies" 
  },
  { 
    name: "Government & Public Sector", 
    description: "Federal, state, local agencies, defense, public administration" 
  },
  { 
    name: "Education", 
    description: "Higher education, K-12, vocational, edtech institutions" 
  },
  { 
    name: "Energy & Utilities", 
    description: "Oil & gas, renewable energy, mining, electricity, water, waste management" 
  },
  { 
    name: "Transportation & Logistics", 
    description: "Airlines, rail, shipping, trucking, warehousing, supply chain" 
  },
  { 
    name: "Telecommunications", 
    description: "Wireless, broadband, satellite, telecom infrastructure" 
  },
  { 
    name: "Hospitality & Travel", 
    description: "Hotels, resorts, airlines, cruise lines, travel services" 
  },
  { 
    name: "Media & Entertainment", 
    description: "Broadcasting, streaming, gaming, publishing, sports, events" 
  },
  { 
    name: "Construction & Engineering", 
    description: "Commercial, residential, infrastructure, civil engineering" 
  },
  { 
    name: "Non-Profit & NGOs", 
    description: "Charities, foundations, advocacy groups, international NGOs" 
  }
];

export const INDUSTRY_DEFAULTS: Record<IndustryName, IndustryDefault> = {
  "Technology": {
    tasScore: 52,
    topPerformerRange: [70, 85],
    timeToFill: 33,
    costPerHire: 5000,
    dailyVacancyCost: 475, // average of 450-500
    avgSalary: 90000,
    avgConfirmationTime: 48,
    adminTimePerHire: 180,
    reschedulingRate: 22.5, // average of 20-25
    interviewNoShowRate: 12.5, // average of 10-15
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.40, // 40%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.85,
    ttfAttributionFactor: 0.25,
    roleVariabilityDiscount: 0.20
  },
  "Financial Services": {
    tasScore: 48,
    topPerformerRange: [65, 80],
    timeToFill: 45,
    costPerHire: 5000,
    dailyVacancyCost: 400,
    avgSalary: 75000,
    avgConfirmationTime: 60, // average of 48-72
    adminTimePerHire: 150,
    reschedulingRate: 23,
    interviewNoShowRate: 12.5, // average of 10-15
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.20, // 20%
    // Time-to-Value metrics
    implementationComplexity: 'complex',
    defaultImplementationWeeks: 12,
    defaultAdoptionMonths: 3,
    typicalRealizationFactor: 0.75,
    ttfAttributionFactor: 0.20,
    roleVariabilityDiscount: 0.30
  },
  "Healthcare Providers & Services": {
    tasScore: 45,
    topPerformerRange: [60, 75],
    timeToFill: 49,
    costPerHire: 5000,
    dailyVacancyCost: 375, // average of 350-400
    avgSalary: 75000,
    avgConfirmationTime: 48,
    adminTimePerHire: 120,
    reschedulingRate: 22.5, // average of 20-25
    interviewNoShowRate: 15,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.25, // 25%
    // Time-to-Value metrics
    implementationComplexity: 'complex',
    defaultImplementationWeeks: 15,
    defaultAdoptionMonths: 3,
    typicalRealizationFactor: 0.70,
    ttfAttributionFactor: 0.15,
    roleVariabilityDiscount: 0.35
  },
  "Pharmaceuticals & Life Sciences": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 50,
    costPerHire: 6000,
    dailyVacancyCost: 500,
    avgSalary: 100000,
    avgConfirmationTime: 60, // average of 48-72
    adminTimePerHire: 150,
    reschedulingRate: 22.5, // average of 20-25
    interviewNoShowRate: 12,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.20, // 20%
    // Time-to-Value metrics
    implementationComplexity: 'complex',
    defaultImplementationWeeks: 12,
    defaultAdoptionMonths: 3,
    typicalRealizationFactor: 0.75,
    ttfAttributionFactor: 0.18,
    roleVariabilityDiscount: 0.30
  },
  "Manufacturing": {
    tasScore: 43,
    topPerformerRange: [58, 72],
    timeToFill: 31,
    costPerHire: 3500,
    dailyVacancyCost: 300,
    avgSalary: 60000,
    avgConfirmationTime: 42, // average of 36-48
    adminTimePerHire: 105, // average of 90-120
    reschedulingRate: 20,
    interviewNoShowRate: 17.5, // average of 15-20
    candidateSatisfaction: 4,
    interviewerSatisfaction: 4,
    interviewerAvailability: 5,
    turnoverRate: 0.28, // 28%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.20,
    roleVariabilityDiscount: 0.25
  },
  "Retail & Consumer Goods": {
    tasScore: 47,
    topPerformerRange: [62, 78],
    timeToFill: 25,
    costPerHire: 2000,
    dailyVacancyCost: 200,
    avgSalary: 40000,
    avgConfirmationTime: 36, // average of 24-48
    adminTimePerHire: 60,
    reschedulingRate: 15,
    interviewNoShowRate: 30,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.33, // 33%
    // Time-to-Value metrics
    implementationComplexity: 'simple',
    defaultImplementationWeeks: 8,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.85,
    ttfAttributionFactor: 0.25,
    roleVariabilityDiscount: 0.20
  },
  "Professional & Business Services": {
    tasScore: 51,
    topPerformerRange: [68, 82],
    timeToFill: 27.5, // average of 25-30
    costPerHire: 6000,
    dailyVacancyCost: 420,
    avgSalary: 80000,
    avgConfirmationTime: 60, // average of 48-72
    adminTimePerHire: 165, // average of 150-180
    reschedulingRate: 25,
    interviewNoShowRate: 10,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.20, // 20%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.22,
    roleVariabilityDiscount: 0.25
  },
  "Government & Public Sector": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 75, // average of 60-90
    costPerHire: 5000,
    dailyVacancyCost: 350,
    avgSalary: 70000,
    avgConfirmationTime: 72,
    adminTimePerHire: 150,
    reschedulingRate: 20,
    interviewNoShowRate: 10,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.15, // 15%
    // Time-to-Value metrics
    implementationComplexity: 'complex',
    defaultImplementationWeeks: 15,
    defaultAdoptionMonths: 4,
    typicalRealizationFactor: 0.70,
    ttfAttributionFactor: 0.15,
    roleVariabilityDiscount: 0.35
  },
  "Education": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 55, // average of 50-60
    costPerHire: 4000,
    dailyVacancyCost: 300,
    avgSalary: 60000,
    avgConfirmationTime: 72,
    adminTimePerHire: 150,
    reschedulingRate: 20,
    interviewNoShowRate: 10,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.15, // 15%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.20,
    roleVariabilityDiscount: 0.25
  },
  "Energy & Utilities": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 50,
    costPerHire: 6000,
    dailyVacancyCost: 500,
    avgSalary: 100000,
    avgConfirmationTime: 60, // average of 48-72
    adminTimePerHire: 150,
    reschedulingRate: 22.5, // average of 20-25
    interviewNoShowRate: 10,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.18, // 18%
    // Time-to-Value metrics
    implementationComplexity: 'complex',
    defaultImplementationWeeks: 12,
    defaultAdoptionMonths: 3,
    typicalRealizationFactor: 0.75,
    ttfAttributionFactor: 0.18,
    roleVariabilityDiscount: 0.30
  },
  "Transportation & Logistics": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 35,
    costPerHire: 3000,
    dailyVacancyCost: 250,
    avgSalary: 50000,
    avgConfirmationTime: 42, // average of 36-48
    adminTimePerHire: 105, // average of 90-120
    reschedulingRate: 20,
    interviewNoShowRate: 17.5, // average of 15-20
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.28, // 28%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.20,
    roleVariabilityDiscount: 0.25
  },
  "Telecommunications": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 40,
    costPerHire: 4000,
    dailyVacancyCost: 350,
    avgSalary: 70000,
    avgConfirmationTime: 48,
    adminTimePerHire: 120,
    reschedulingRate: 20,
    interviewNoShowRate: 12,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.20, // 20%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.22,
    roleVariabilityDiscount: 0.25
  },
  "Hospitality & Travel": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 25,
    costPerHire: 2500,
    dailyVacancyCost: 200,
    avgSalary: 40000,
    avgConfirmationTime: 36, // average of 24-48
    adminTimePerHire: 60,
    reschedulingRate: 15,
    interviewNoShowRate: 30, // average of 25-35
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.35, // 35%
    // Time-to-Value metrics
    implementationComplexity: 'simple',
    defaultImplementationWeeks: 8,
    defaultAdoptionMonths: 1,
    typicalRealizationFactor: 0.90,
    ttfAttributionFactor: 0.30,
    roleVariabilityDiscount: 0.15
  },
  "Media & Entertainment": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 35,
    costPerHire: 4000,
    dailyVacancyCost: 350,
    avgSalary: 70000,
    avgConfirmationTime: 48,
    adminTimePerHire: 120,
    reschedulingRate: 20,
    interviewNoShowRate: 12,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.20, // 20%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.22,
    roleVariabilityDiscount: 0.25
  },
  "Construction & Engineering": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 40,
    costPerHire: 4500,
    dailyVacancyCost: 350,
    avgSalary: 70000,
    avgConfirmationTime: 42, // average of 36-48
    adminTimePerHire: 120,
    reschedulingRate: 20,
    interviewNoShowRate: 12,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.25, // 25%
    // Time-to-Value metrics
    implementationComplexity: 'standard',
    defaultImplementationWeeks: 10,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.80,
    ttfAttributionFactor: 0.20,
    roleVariabilityDiscount: 0.25
  },
  "Non-Profit & NGOs": {
    tasScore: 50,
    topPerformerRange: [65, 80],
    timeToFill: 50,
    costPerHire: 3500,
    dailyVacancyCost: 250,
    avgSalary: 50000,
    avgConfirmationTime: 60, // average of 48-72
    adminTimePerHire: 120,
    reschedulingRate: 20,
    interviewNoShowRate: 12,
    candidateSatisfaction: 5,
    interviewerSatisfaction: 5,
    interviewerAvailability: 5,
    turnoverRate: 0.18, // 18%
    // Time-to-Value metrics
    implementationComplexity: 'simple',
    defaultImplementationWeeks: 8,
    defaultAdoptionMonths: 2,
    typicalRealizationFactor: 0.85,
    ttfAttributionFactor: 0.20,
    roleVariabilityDiscount: 0.20
  }
};

/**
 * Get the list of available industries
 */
export const getIndustryList = (): IndustryName[] => {
  return Object.keys(INDUSTRY_DEFAULTS) as IndustryName[];
};

/**
 * Get defaults for a specific industry
 */
export const getIndustryDefaults = (industry: IndustryName): IndustryDefault | undefined => {
  return INDUSTRY_DEFAULTS[industry];
};

/**
 * Convert FTE (Full-Time Employees) to Annual Hires using industry turnover rate
 * Formula: Annual Hires = FTE × Turnover Rate
 * @param fte - Number of full-time employees
 * @param industry - Industry name for specific turnover rate
 * @returns Estimated annual hires
 */
export const convertFTEtoAnnualHires = (
  fte: number, 
  industry: IndustryName
): number => {
  const defaults = INDUSTRY_DEFAULTS[industry];
  if (!defaults) return 0;
  return Math.round(fte * defaults.turnoverRate);
};

/**
 * Convert Annual Hires to estimated FTE using industry turnover rate
 * Formula: FTE = Annual Hires ÷ Turnover Rate
 * @param annualHires - Number of annual hires
 * @param industry - Industry name for specific turnover rate
 * @returns Estimated FTE count
 */
export const convertAnnualHiresToFTE = (
  annualHires: number, 
  industry: IndustryName
): number => {
  const defaults = INDUSTRY_DEFAULTS[industry];
  if (!defaults || defaults.turnoverRate === 0) return 0;
  return Math.round(annualHires / defaults.turnoverRate);
};